import React, { useState, useEffect } from "react";
import { useAccount, useDisconnect } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, ShieldCheck, AlertCircle, KeyRound, Info } from "lucide-react";
import logo from "@/assets/images/logo.svg";
import { useAuth, AUTH_ERROR_CODES } from "@/hooks/useAuth";
import { useAuthStore } from "@/store/useAuthStore";
import { checkWalletRegistered } from "@/services/api/auth";
import { REQUIRE_ACCESS_CODE } from "@/config/constants";

interface WalletGateProps {
  children: React.ReactNode;
}

function InfoBox({
  icon: Icon,
  title,
  description,
  variant = "default",
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  variant?: "default" | "error";
}) {
  const isError = variant === "error";
  return (
    <div
      className={`flex gap-3 rounded-lg border p-3 ${
        isError
          ? "border-destructive/30 bg-destructive/5"
          : "border-border/50 bg-muted/30"
      }`}
    >
      <Icon
        className={`h-4 w-4 mt-0.5 shrink-0 ${
          isError ? "text-destructive" : "text-muted-foreground"
        }`}
      />
      <div className="space-y-0.5">
        <p
          className={`text-sm font-medium leading-none ${
            isError ? "text-destructive" : "text-foreground"
          }`}
        >
          {title}
        </p>
        <p
          className={`text-xs leading-relaxed ${
            isError ? "text-destructive/80" : "text-muted-foreground"
          }`}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function AccessCodeWalletGate({ children }: WalletGateProps) {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { open } = useAppKit();
  const { login, logout, isLoading, error, clearError } = useAuth();
  const walletAddress = useAuthStore((s) => s.walletAddress);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const expiresAt = useAuthStore((s) => s.expiresAt);

  // Derive isAuthenticated locally so it re-evaluates on each render
  // and on the interval tick below — Zustand selectors calling Date.now()
  // won't re-render because the store state hasn't changed.
  const isAuthenticated = (() => {
    if (!expiresAt) return false;
    const expiryTime = new Date(expiresAt).getTime();
    if (Number.isNaN(expiryTime)) return false;
    return Date.now() < expiryTime;
  })();

  // Force re-render periodically to catch session expiry
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const [accessCode, setAccessCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);

  // Check if wallet is already registered
  useEffect(() => {
    if (!address) {
      setIsRegistered(null);
      return;
    }

    let cancelled = false;
    checkWalletRegistered(address).then((registered) => {
      if (!cancelled) setIsRegistered(registered);
    });
    return () => { cancelled = true; };
  }, [address]);

  // Clear auth if wallet changes
  useEffect(() => {
    if (address && walletAddress && address.toLowerCase() !== walletAddress) {
      clearAuth();
    }
  }, [address, walletAddress, clearAuth]);

  // Clear auth if wallet disconnects
  useEffect(() => {
    if (!isConnected && walletAddress) {
      clearAuth();
    }
  }, [isConnected, walletAddress, clearAuth]);

  // Clear errors when access code changes
  useEffect(() => {
    if (accessCode) {
      setLocalError(null);
      clearError();
    }
  }, [accessCode, clearError]);

  const handleAuthenticate = async () => {
    setLocalError(null);

    try {
      await login(accessCode || undefined);
    } catch (err) {
      // Error is handled by useAuth hook
    }
  };

  // Get user-friendly error message
  const getErrorMessage = (): string | null => {
    if (localError) return localError;
    if (!error) return null;

    switch (error.code) {
      case AUTH_ERROR_CODES.ACCESS_CODE_REQUIRED:
        return "An access code is required to sign up. Please enter your access code below.";
      case AUTH_ERROR_CODES.INVALID_OR_USED_CODE:
        return "Invalid or already used code. Please check and try again.";
      case AUTH_ERROR_CODES.WALLET_ALREADY_REGISTERED:
        return "This wallet is already registered. Clear the access code field to sign in.";
      case AUTH_ERROR_CODES.INVALID_SIGNATURE:
        return "Signature verification failed. Please try again.";
      case AUTH_ERROR_CODES.SESSION_EXPIRED:
        return "Session expired. Please try again.";
      case "USER_REJECTED":
        return "Signature request was rejected. Please try again.";
      default:
        return error.message || "An error occurred. Please try again.";
    }
  };

  const errorMessage = getErrorMessage();

  // If authenticated with valid session, render children
  if (isAuthenticated && address) {
    return <>{children}</>;
  }

  // If not connected, show connect prompt
  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center pb-2">
            <img src={logo} alt="Helm" className="h-8 w-auto mx-auto mb-4" />
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto mb-3">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">Welcome to Helm</CardTitle>
            <CardDescription>
              Connect your wallet to access the platform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-2">
            <InfoBox
              icon={Info}
              title="Secure Sign-In"
              description="You'll sign a message to verify wallet ownership. This is free and doesn't submit a transaction."
            />
          </CardContent>
          <CardFooter>
            <Button onClick={() => open()} className="w-full" size="lg">
              <Wallet className="mr-2 h-4 w-4" />
              Connect Wallet
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Wait for wallet registration check to complete before rendering form
  if (isRegistered === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If connected but not authenticated, show sign-in form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center pb-2">
          <img src={logo} alt="Helm" className="h-8 w-auto mx-auto mb-4" />
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto mb-3">
            <Wallet className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">Verify Wallet Ownership</CardTitle>
          <CardDescription>
            Sign a message to confirm you own this wallet
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {/* Connected Wallet Display */}
          <div className="flex items-center justify-between rounded-lg border border-border/50 bg-muted/30 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <span className="text-sm text-muted-foreground">Connected</span>
            </div>
            <span className="font-mono text-sm">
              {address && truncateAddress(address)}
            </span>
          </div>

          {/* Access Code Input - hidden for already registered wallets */}
          {REQUIRE_ACCESS_CODE && !isRegistered && (
            <div className="space-y-2">
              <Label htmlFor="accessCode" className="flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                Access Code
              </Label>
              <Input
                id="accessCode"
                type="text"
                placeholder="Enter your access code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                className="font-mono uppercase tracking-wider"
                disabled={isLoading}
                autoComplete="off"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Required for new users during early access
              </p>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <InfoBox
              icon={AlertCircle}
              title="Error"
              description={errorMessage}
              variant="error"
            />
          )}

          {/* Info Box */}
          <InfoBox
            icon={Info}
            title="No gas fees"
            description="Signing is free and doesn't submit a blockchain transaction."
          />
        </CardContent>

        <CardFooter className="flex-col gap-2">
          <Button
            onClick={handleAuthenticate}
            disabled={isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? "Signing..." : "Sign Message to Continue"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => {
              logout();
              disconnect();
            }}
          >
            Disconnect
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
